/**
 * ET PDP fulfilment, September 2026.
 * One state resolver owns stock, dispatch and delivery copy. Legacy PDP entry
 * points delegate here so a variant change cannot restore a stale countdown.
 * This component is informational: it never changes inventory, cart quantities,
 * prices, purchase availability, or tracking events.
 *
 * The stock and dispatch rules live in pdp-fulfilment-core.js, shared with the
 * BN theme and loaded just before this file. NSW holidays are computed there,
 * so the countdown does not lapse when a hard-coded list runs out. Supplier
 * lead-time exceptions come from the product metafield
 * custom.lead_time_unconfirmed rather than variant IDs in code.
 */
(function (global) {
  'use strict';
  const core = () => global.PDPFulfilment;
  // Kept for regression tests and callers that used the old exports.
  const stockState = (...args) => core().stockState(...args);
  const dispatchState = (...args) => core().dispatchState(...args);

  const configs = new WeakMap();
  let cartQuantities = new Map();
  let cartRequest = null;
  function text(node, value) {
    // Avoid redundant DOM writes and repeated screen-reader announcements.
    if (node && node.textContent !== value) node.textContent = value;
  }
  function format(template, token, value) {
    return String(template || '').replaceAll(`__${token}__`, String(value));
  }
  function configuration(box) {
    if (!configs.has(box)) {
      try { configs.set(box, JSON.parse(box.querySelector('[data-fulfilment-config]').textContent)); }
      catch { return null; } // Keep the safe Liquid fallback if configuration fails.
    }
    return configs.get(box);
  }

  /** Render every matching box within the supplied section/document.
   * A supplied variant is matched against section-scoped inventory data; the
   * generic product JSON often omits inventory_policy and inventory_quantity.
   */
  function render(root, suppliedVariant) {
    // Without the shared core, keep the server-rendered Liquid rows as they are.
    if (!root || !root.querySelectorAll || !core()) return;
    for (const box of root.querySelectorAll('[data-fulfilment]')) {
      const config = configuration(box);
      const section = box.closest('.pro-product');
      if (!config || !section) continue;
      // Recommendation cards also contain cart forms; use only this PDP form.
      const input = section.querySelector('.pro-product__form [data-variant-id-input]');
      // A variant event from another product section must not blank this box.
      const suppliedId = suppliedVariant?.id;
      if (suppliedId && !config.variants.some(item => String(item.id) === String(suppliedId))) continue;
      const id = String(suppliedId || input?.value || box.dataset.selectedVariant);
      const variant = config.variants.find(item => String(item.id) === id);
      const quantityInput = section.querySelector('.pro-product__form [data-qty-input]');
      const requested = Math.max(1, Number(quantityInput?.value) || 1) + (cartQuantities.get(id) || 0);
      const state = stockState(variant, requested, Number(section.dataset.lowStockThreshold) || 2);
      const copy = config.copy;
      let stock = copy[state.kind] || copy.untracked;
      let dispatch = '';
      if (state.kind === 'low' || state.kind === 'limited') stock = format(stock, 'COUNT', state.qty);
      if (state.kind === 'supplier') {
        stock = state.mixed ? format(copy.mixed, 'COUNT', state.qty) : copy.supplier;
        dispatch = state.uncertain ? copy.uncertain : copy.supplier_time;
      } else if (state.kind === 'stocked' || state.kind === 'low') {
        const schedule = dispatchState(new Date(), section.dataset.fulfilmentCutoff,
          section.dataset.holidays, section.dataset.fulfilmentTimezone || 'Australia/Sydney');
        dispatch = schedule.kind === 'today' ? format(copy.today, 'TIME', schedule.time)
          : schedule.kind === 'next' ? format(copy.next, 'DATE', schedule.date) : copy.fallback;
      } else if (state.kind === 'untracked') {
        dispatch = copy.fallback;
      } else if (state.kind === 'limited') {
        dispatch = cartQuantities.get(id) > 0 ? copy.reduce_cart : copy.reduce;
      }
      // Templates with their own availability wording (the Discovery Box) keep it
      // whenever the variant can be bought from stock.
      if (config.stock_override && ['stocked', 'low', 'untracked'].includes(state.kind)) {
        stock = config.stock_override;
      }
      if (config.hide_dispatch) dispatch = '';
      box.dataset.fulfilmentState = state.kind;
      const stockRow = box.querySelector('.pro-fulfilment__stock');
      stockRow.className = `pro-fulfilment__row pro-fulfilment__stock is-${state.kind}`;
      text(box.querySelector('[data-fulfilment-stock-mirror]'), stock);
      text(box.querySelector('[data-fulfilment-countdown]'), dispatch);
      box.querySelector('.pro-fulfilment__dispatch').hidden = !dispatch;
      // Postcode and carrier eligibility are not known here. Never imply a
      // basket has unlocked free freight from its monetary subtotal alone.
      text(box.querySelector('[data-free-shipping-progress]'), copy.shipping);
    }
  }

  /** Read-only cart lookup catches quantity already present in the basket.
   * Deduplicate requests; failure does not block the PDP or invent cart data.
   */
  function refreshCart() {
    if (cartRequest || typeof global.fetch !== 'function') return cartRequest;
    const root = global.Shopify?.routes?.root || '/';
    cartRequest = global.fetch(`${root}cart.js`, { credentials: 'same-origin' })
      .then(response => { if (!response.ok) throw new Error('Cart unavailable'); return response.json(); })
      .then(cart => {
        const next = new Map();
        for (const item of cart.items || []) {
          const id = String(item.variant_id);
          next.set(id, (next.get(id) || 0) + Number(item.quantity || 0));
        }
        cartQuantities = next;
      }).catch(() => {}).finally(() => { cartRequest = null; render(document); });
    return cartRequest;
  }

  // Expose pure functions for regression tests and one renderer for legacy hooks.
  global.ETFulfilment = { render, stockState, dispatchState };
  if (typeof document === 'undefined') return;
  function boot() { render(document); refreshCart(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
  for (const eventName of ['input', 'change']) document.addEventListener(eventName, event => {
    if (event.target.matches?.('.pro-product [name="quantity"], .pro-product [name="id"]')) render(document);
  });
  // Existing quantity buttons may change .value without emitting input/change.
  document.addEventListener('click', event => {
    if (event.target.closest?.('.pro-product [data-qty]')) global.requestAnimationFrame(() => render(document));
  });
  for (const eventName of ['cart:updated', 'ajaxProduct:added']) document.addEventListener(eventName, refreshCart);
})(typeof window === 'undefined' ? globalThis : window);
/* END ET FULFILMENT CONTROLLER */
